import { Skeleton, TextField } from '@mui/material';
import { FC, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import FieldEditComponent from 'components/Atoms/Form/FieldEditComponent/FieldEditComponent';
import { UpdateCompanyMutationFn, UpdateMyCompanyMutationFn } from 'src/graphql/generated/hooks';
import { UpdateMyCompanyInput } from 'src/graphql/generated/schema';

import { AdminCompanyDetailsQuery, MyCompanyDetailsQuery } from '../../graphql/generated/operations';

type FormInput = Pick<UpdateMyCompanyInput, 'phoneNumber'>;

interface EditCompanyPhoneNumberProps {
  data: MyCompanyDetailsQuery['me'] | AdminCompanyDetailsQuery | undefined;
  loading: boolean;
  mutation: UpdateMyCompanyMutationFn | UpdateCompanyMutationFn;
  companyId?: string;
}

const EditCompanyPhoneNumber: FC<EditCompanyPhoneNumberProps> = ({ data, loading, mutation, companyId }) => {
  const { t } = useTranslation('translation');

  const { getValues, control, reset } = useForm<FormInput>({
    values: { phoneNumber: data?.company.phoneNumber ?? '' },
  });

  const onSubmit = useCallback(() => {
    mutation({
      variables: { input: { companyId, phoneNumber: getValues('phoneNumber') } },
      update(_, { data }) {
        if (data) {
          toast.success(t('messages.success.company-phone-number-updated'));
        }
      },
    });
  }, [companyId, getValues, mutation, t]);

  if (loading || !data) {
    return (
      <>
        <Skeleton />
        <Skeleton />
      </>
    );
  }

  return (
    <FieldEditComponent
      title={t('pages.company-details-page.phone-number')}
      value={getValues('phoneNumber')}
      onCancel={reset}
      onSave={onSubmit}
    >
      <Controller
        control={control}
        defaultValue={data.company.phoneNumber}
        name="phoneNumber"
        render={({ field }) => (
          <TextField {...field} fullWidth inputProps={{ 'data-testid': 'input-company-phoneNumber' }} />
        )}
        rules={{ required: true }}
      />
    </FieldEditComponent>
  );
};

export default EditCompanyPhoneNumber;
